/**
 * Build an ATS-optimized DOCX document from a CVData.
 *
 * Pure function — takes all its dependencies (data, field visibility, and
 * format helpers that depend on i18n or component-scoped helpers) as params.
 * Returns a Blob ready to be passed to saveAs / download.
 *
 * Import is dynamic so the `docx` package (~200 KB) stays out of the initial
 * bundle.
 */

import type { CVData } from '../types';
import type { FieldVisibility } from '../hooks/useCvState';

export interface BuildAtsCvDocxParams {
  data: CVData;
  fieldVisibility: Pick<FieldVisibility, 'civility' | 'firstName' | 'lastName'>;
  /** Pre-computed ATS contact line (e.g. "email | phone | city"). */
  contactLine: string;
  /** Pre-computed sanitized full name, used internally if needed. */
  fullName: string;
  /** Extract a trailing city from a company string like "ACME, Paris". */
  extractCityFromCompany: (company: string) => string;
  /** Expand abbreviated month patterns to a French display form. */
  formatDateWithFullMonth: (dateStr: string) => string;
  /** Format an education entry's date range for display. */
  formatEducationDate: (edu: { date?: string; startDate?: string; endDate?: string }) => string;
}

// We keep docx types loose because the package is imported dynamically.
type DocxChild = object;

export async function buildAtsCvDocx(params: BuildAtsCvDocxParams): Promise<Blob> {
  const {
    data,
    fieldVisibility,
    contactLine,
    extractCityFromCompany,
    formatDateWithFullMonth,
    formatEducationDate,
  } = params;
  void params.fullName; // consumer usually also uses fullName for the filename

  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } =
    await import('docx');

  const children: DocxChild[] = [];

  // === EN-TÊTE (Nom + Contact, champs visibles uniquement) ===
  const civ =
    fieldVisibility.civility &&
    (data.personalInfo.civility === 'M.' || data.personalInfo.civility === 'Mme.')
      ? data.personalInfo.civility
      : '';
  const fn = fieldVisibility.firstName ? (data.personalInfo.firstName ?? '').trim() : '';
  const ln = fieldVisibility.lastName ? (data.personalInfo.lastName ?? '').trim() : '';
  const hasName = civ || fn || ln;
  if (hasName) {
    const atsName = [civ, fn ? `Prénom: ${fn}` : '', ln ? `Nom: ${ln}` : '']
      .filter(Boolean)
      .join(' ');
    children.push(
      new Paragraph({
        children: [new TextRun({ text: atsName, bold: true, size: 32 })],
        alignment: AlignmentType.LEFT,
        spacing: { after: 100 },
      }),
    );
  }
  children.push(
    new Paragraph({
      children: [new TextRun({ text: data.personalInfo.title, size: 24 })],
      spacing: { after: 200 },
    }),
  );
  if (contactLine) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: contactLine, size: 20 })],
        spacing: { after: 300 },
      }),
    );
  }

  // === PROFIL / RÉSUMÉ ===
  if (data.personalInfo.summary) {
    children.push(
      new Paragraph({
        text: 'PROFIL PROFESSIONNEL',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 180, after: 80 },
        border: { bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 } },
      }),
      new Paragraph({
        children: [new TextRun({ text: data.personalInfo.summary, size: 22 })],
        spacing: { after: 300 },
      }),
    );
  }

  // === EXPÉRIENCE PROFESSIONNELLE ===
  if (data.experience.length > 0) {
    children.push(
      new Paragraph({
        text: 'EXPERIENCE PROFESSIONNELLE',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 180, after: 80 },
        border: { bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 } },
      }),
    );

    data.experience.forEach((exp) => {
      const city = extractCityFromCompany(exp.company);
      const companyName = city ? exp.company.replace(`, ${city}`, '').trim() : exp.company;
      const formattedStartDate = formatDateWithFullMonth(exp.startDate);
      const formattedEndDate = formatDateWithFullMonth(exp.endDate);

      const experienceLine = [new TextRun({ text: exp.position, bold: true, size: 22 })];
      if (companyName) {
        experienceLine.push(new TextRun({ text: `, chez ${companyName}`, size: 22 }));
      }
      if (city) {
        experienceLine.push(
          new TextRun({ text: `, à ${city}`, size: 22, color: '666666' }),
        );
      }
      if (formattedStartDate || formattedEndDate) {
        experienceLine.push(
          new TextRun({
            text: `, ${formattedStartDate} à ${formattedEndDate}`,
            size: 20,
            italics: true,
            color: '666666',
          }),
        );
      }

      children.push(
        new Paragraph({
          children: experienceLine,
          spacing: { before: 80, after: 40 },
        }),
      );

      const bullets = exp.description.split('\n').filter((line) => line.trim());
      bullets.forEach((bullet) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `• ${bullet.replace(/^[-•*]\s*/, '')}`, size: 20 }),
            ],
            spacing: { after: 50 },
            indent: { left: 360 },
          }),
        );
      });
    });
  }

  // === FORMATION ===
  if (data.education.length > 0) {
    children.push(
      new Paragraph({
        text: 'FORMATION',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 180, after: 80 },
        border: { bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 } },
      }),
    );

    data.education.forEach((edu) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: edu.degree, bold: true, size: 22 }),
            new TextRun({
              text: formatEducationDate(edu) ? ` (${formatEducationDate(edu)})` : '',
              size: 20,
              color: '666666',
            }),
          ],
          spacing: { before: 150, after: 30 },
        }),
        new Paragraph({
          children: [new TextRun({ text: edu.school, size: 20, color: '444444' })],
          spacing: { after: 100 },
        }),
      );
    });
  }

  // === COMPÉTENCES ===
  if (data.skills.length > 0) {
    children.push(
      new Paragraph({
        text: 'COMPETENCES',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 180, after: 80 },
        border: { bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 } },
      }),
      new Paragraph({
        children: [new TextRun({ text: data.skills.join(' • '), size: 20 })],
        spacing: { after: 200 },
      }),
    );
  }

  // === LANGUES ===
  if (data.languages.length > 0) {
    children.push(
      new Paragraph({
        text: 'LANGUES',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 180, after: 80 },
        border: { bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 } },
      }),
      new Paragraph({
        children: [new TextRun({ text: data.languages.join(' • '), size: 20 })],
        spacing: { after: 200 },
      }),
    );
  }

  // === CENTRES D'INTÉRÊT ===
  if (data.hobbies && data.hobbies.length > 0) {
    children.push(
      new Paragraph({
        text: "CENTRES D'INTERET",
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 180, after: 80 },
        border: { bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 } },
      }),
      new Paragraph({
        children: [new TextRun({ text: data.hobbies.join(' • '), size: 20 })],
        spacing: { after: 200 },
      }),
    );
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            // Tight margins for ATS output.
            margin: { top: 360, right: 360, bottom: 360, left: 360 },
          },
        },
        children: children as never[],
      },
    ],
    styles: {
      default: {
        document: {
          run: { font: 'Calibri' },
        },
      },
    },
  });

  return Packer.toBlob(doc);
}
